import { usePage } from "@inertiajs/react"
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react"

import { BREAKPOINTS } from "@/hooks/use-breakpoint"

const ASSISTANT_STORAGE_KEY = "assistant"
const SIDEBAR_STORAGE_KEY = "sidebar"

interface AssistantContextValue {
  assistantOpen: boolean
  openAssistant: () => void
  closeAssistant: () => void
  toggleAssistant: () => void
  sidebarOpen: boolean
  setSidebarOpen: (open: boolean) => void
}

const AssistantContext = createContext<AssistantContextValue | null>(null)

/**
 * Whether this desk has the in-app assistant at all (INCO-512). The server
 * decides, per desk, and shares it on every page; off means no trigger and no
 * panel, whatever localStorage remembers about the panel having been open.
 */
export function useAssistantEnabled(): boolean {
  const { props } = usePage()
  const features = props.features as { assistant?: boolean } | undefined
  return Boolean(features?.assistant)
}

export function useAssistant(): AssistantContextValue {
  const ctx = useContext(AssistantContext)
  if (!ctx) {
    throw new Error("useAssistant must be used within an AssistantProvider")
  }
  return ctx
}

function readBoolFromStorage(key: string, defaultValue: boolean): boolean {
  if (typeof window === "undefined") return defaultValue
  const raw = window.localStorage.getItem(key)
  if (raw === null) return defaultValue
  return raw === "true"
}

export function AssistantProvider({ children }: { children: ReactNode }) {
  const enabled = useAssistantEnabled()
  const [assistantOpenState, setAssistantOpenState] = useState(() =>
    readBoolFromStorage(ASSISTANT_STORAGE_KEY, false),
  )
  // A browser that last saw the panel open remembers it. On a desk with the
  // assistant off that memory counts for nothing (INCO-512), and it is left
  // in storage so the panel comes back as it was if the desk is switched on.
  const assistantOpen = enabled && assistantOpenState
  const [sidebarOpen, setSidebarOpenState] = useState(() => {
    if (typeof window === "undefined") return true
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) !== "false"
  })

  const setSidebarOpen = useCallback((open: boolean) => {
    setSidebarOpenState(open)
    if (typeof window !== "undefined") {
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(open))
    }
  }, [])

  const openAssistant = useCallback(() => {
    setAssistantOpenState(true)
    if (typeof window !== "undefined") {
      window.localStorage.setItem(ASSISTANT_STORAGE_KEY, "true")
      if (window.innerWidth < BREAKPOINTS.md) {
        setSidebarOpen(false)
      }
    }
  }, [setSidebarOpen])

  const closeAssistant = useCallback(() => {
    setAssistantOpenState(false)
    if (typeof window !== "undefined") {
      window.localStorage.setItem(ASSISTANT_STORAGE_KEY, "false")
    }
  }, [])

  const toggleAssistant = useCallback(() => {
    if (assistantOpen) closeAssistant()
    else openAssistant()
  }, [assistantOpen, openAssistant, closeAssistant])

  const value = useMemo<AssistantContextValue>(
    () => ({
      assistantOpen,
      openAssistant,
      closeAssistant,
      toggleAssistant,
      sidebarOpen,
      setSidebarOpen,
    }),
    [
      assistantOpen,
      openAssistant,
      closeAssistant,
      toggleAssistant,
      sidebarOpen,
      setSidebarOpen,
    ],
  )

  return (
    <AssistantContext.Provider value={value}>
      {children}
    </AssistantContext.Provider>
  )
}
